/** The reference library — Grammatikbank and Wortschatzbank.
 *
 * Deliberately reachable WITHOUT walking the lesson path. A learner
 * revising Akkusativ the night before an exam needs the table, not the
 * lesson that introduced it; and a teacher wants to point at a rule in
 * class. Level and tab both live in the URL so those links are shareable.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as api from "../../api/reference";
import type { GrammarLevel, VocabLevel } from "../../api/reference";
import { Card, EmptyState, Input, Spinner } from "../../components/ui";
import { TapToTranslate } from "../../components/TapToTranslate";
import { useT } from "../../i18n";
import { GrammarTableView } from "./GrammarTableView";
import { VocabReview } from "./VocabReview";

type Tab = "grammar" | "vocab" | "review";

export function ReferencePage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const level = (params.get("level") || "").toUpperCase();
  const raw = params.get("tab");
  const tab: Tab = raw === "vocab" || raw === "review" ? raw : "grammar";
  const [query, setQuery] = useState("");

  const [levels, setLevels] = useState<string[] | null>(null);
  const [grammar, setGrammar] = useState<GrammarLevel | null>(null);
  const [vocab, setVocab] = useState<VocabLevel | null>(null);

  useEffect(() => {
    api.referenceLevels().then(setLevels).catch(() => setLevels([]));
  }, []);

  // Default to the first level once we know what exists.
  useEffect(() => {
    if (levels && levels.length > 0 && !level) {
      setParams({ level: levels[0], tab }, { replace: true });
    }
  }, [levels, level, tab, setParams]);

  useEffect(() => {
    if (!level) return;
    setGrammar(null);
    setVocab(null);
    if (tab === "grammar") {
      api.grammarForLevel(level).then(setGrammar).catch(() => setGrammar(null));
    } else if (tab === "vocab") {
      api.vocabForLevel(level).then(setVocab).catch(() => setVocab(null));
    }
    // the review tab loads its own queue — no word list needed
  }, [level, tab]);

  const select = useCallback(
    (next: Partial<{ level: string; tab: Tab }>) => {
      setParams({ level: next.level ?? level, tab: next.tab ?? tab });
      setQuery("");
    },
    [level, tab, setParams],
  );

  if (!levels) return <Spinner />;

  const needle = query.trim().toLowerCase();
  const topics = (grammar?.topics ?? []).filter(
    (x) =>
      !needle ||
      x.title.toLowerCase().includes(needle) ||
      x.rule.toLowerCase().includes(needle),
  );
  const tables = (grammar?.tables ?? []).filter(
    (x) => !needle || x.title.toLowerCase().includes(needle),
  );
  const themes = (vocab?.themes ?? [])
    .map((th) => ({
      ...th,
      entries: th.entries.filter(
        (e) =>
          !needle ||
          e.de.toLowerCase().includes(needle) ||
          e.en.toLowerCase().includes(needle),
      ),
    }))
    .filter((th) => th.entries.length > 0);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-3xl font-extrabold text-ink">{t("reference.title")}</h1>
        <p className="mt-1 text-muted">{t("reference.subtitle")}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {levels.map((lv) => (
          <button
            key={lv}
            onClick={() => select({ level: lv })}
            className={`rounded-control border px-3 py-1.5 text-sm font-semibold ${
              lv === level
                ? "border-brand bg-brand-soft text-brand-dark"
                : "border-line text-muted hover:text-ink"
            }`}
          >
            {lv}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {(["grammar", "vocab", "review"] as Tab[]).map((tb) => (
            <button
              key={tb}
              onClick={() => select({ tab: tb })}
              className={`rounded-control px-3 py-1.5 text-sm font-semibold ${
                tb === tab ? "bg-brand text-white" : "text-muted hover:text-ink"
              }`}
            >
              {t(
                tb === "grammar"
                  ? "reference.grammar"
                  : tb === "vocab"
                    ? "reference.vocab"
                    : "review.tab",
              )}
            </button>
          ))}
        </div>
        {tab !== "review" && (
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("reference.search")}
          aria-label={t("reference.search")}
          className="max-w-xs"
        />
        )}
      </div>

      {tab === "review" && level && <VocabReview level={level} />}

      {tab === "grammar" && !grammar && <Spinner />}
      {tab === "vocab" && !vocab && <Spinner />}

      {tab === "grammar" && grammar && (
        <div className="flex flex-col gap-4">
          {tables.length === 0 && topics.length === 0 && (
            <EmptyState message={t("reference.noResults")} />
          )}

          {tables.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2 className="text-xl font-bold text-ink">{t("reference.tables")}</h2>
              {tables.map((tb) => (
                <GrammarTableView key={tb.slug} table={tb} />
              ))}
            </section>
          )}

          {topics.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2 className="text-xl font-bold text-ink">{t("reference.rules")}</h2>
              {topics.map((topic) => (
                <Card key={topic.slug}>
                  <h3 className="font-bold text-ink">{topic.title}</h3>
                  {topic.summary && (
                    <p className="mt-1 text-sm text-muted">
                      <TapToTranslate text={topic.summary} />
                    </p>
                  )}
                  <p className="mt-2 text-sm text-ink">
                    <TapToTranslate text={topic.rule} />
                  </p>
                  {topic.examples.length > 0 && (
                    <ul className="mt-3 flex flex-col gap-1">
                      {topic.examples.map((ex, i) => (
                        <li key={i} className="text-sm">
                          <span className="font-semibold text-ink">{ex.de}</span>
                          {ex.en && <span className="text-muted"> — {ex.en}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {topic.tip && (
                    <p className="mt-3 rounded-control bg-brand-soft px-3 py-2 text-sm text-brand-dark">
                      💡 <TapToTranslate text={topic.tip} />
                    </p>
                  )}
                </Card>
              ))}
            </section>
          )}
        </div>
      )}

      {tab === "vocab" && vocab && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            {t("reference.wordCount", { count: vocab.word_count })}
          </p>
          {themes.length === 0 && <EmptyState message={t("reference.noResults")} />}
          {themes.map((th) => (
            <Card key={th.slug}>
              <h3 className="font-bold text-ink">{th.title}</h3>
              <ul className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
                {th.entries.map((e) => (
                  <li key={e.de} className="flex justify-between gap-3 text-sm">
                    <span className="font-medium text-ink">
                      {e.article ? `${e.article} ` : ""}
                      {e.de}
                    </span>
                    <span className="text-right text-muted">{e.en}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
